import { SettingsCollectionModel } from "@insite/client-framework/Services/SettingsService";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import {
    TrackEventBaseParameters,
    TrackEventParameters,
} from "@toolkit/Framework/Common/GoogleAnalytics/trackers/trackEvent";

/* ---------------------------------------------------------------------------------------------------- */
// https://developers.google.com/tag-platform/gtagjs/reference/events#page_view
/* ---------------------------------------------------------------------------------------------------- */

export interface TrackPageViewParameters
    extends TrackEventBaseParameters<
        "page_location" | "client_id" | "language" | "page_encoding" | "page_title" | "user_agent"
    > {
    settingsCollection?: SettingsCollectionModel;
    websiteName?: string;
}

const trackPageView = (parameters: TrackPageViewParameters = {}, options?: Toolkit.GoogleAnalytics.Options) => {
    if (!ga.enabled) {
        return {};
    }

    const {
        action,
        page_encoding: basePageEncoding,
        page_location: basePageLocation,
        page_title: basePageTitle,
        settingsCollection,
        user_agent: baseUserAgent,
        websiteName,
        ...otherProps
    } = parameters;

    let page_encoding = basePageEncoding;
    let page_location = basePageLocation;
    let page_title = basePageTitle;
    let user_agent = baseUserAgent;

    if (typeof window !== "undefined") {
        page_location = page_location || window.location.pathname + window.location.search;
        page_title = page_title || window.document.title;
        page_encoding = page_encoding || window.document.characterSet;
        user_agent = user_agent || window.navigator?.userAgent;
    }

    if (page_title && settingsCollection && websiteName) {
        const {
            websiteSettings: { includeSiteNameInPageTitle, pageTitleDelimiter, siteNameAfterTitle },
        } = settingsCollection;

        // Extract the page title without the website name by undoing this code:
        // modules\client-framework\src\Common\Utilities\setPageMetadata.ts -> generatePageTitle
        if (includeSiteNameInPageTitle) {
            page_title = page_title
                .replace(
                    (siteNameAfterTitle
                        ? `${pageTitleDelimiter}${websiteName}`
                        : `${websiteName}${pageTitleDelimiter}`
                    ).trim(),
                    "",
                )
                .trim();
        }
    }

    const data: TrackEventParameters = {
        action: "page_view",
        page_location,
        page_title,
        page_encoding,
        user_agent,
        ...otherProps,
    };

    return ga.track.event(data, options);
};

export default trackPageView;
